import React, { useState, useEffect } from 'react';
import { MapPin, Edit3 } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from './ui/theme-provider';
import { cn } from '../lib/utils';

const LocationDisplay = ({ 
  userLocation, 
  locationLoading, 
  onUpdateLocation, 
  className = "" 
}) => {
  const { theme } = useTheme();
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [localLocation, setLocalLocation] = useState(userLocation);

  // CRITICAL FIX: Listen for location updates and refresh display
  useEffect(() => {
    const handleLocationUpdate = (event) => {
      console.log('LocationDisplay: Location updated', event.detail.location);
      setLocalLocation(event.detail.location);
    };

    window.addEventListener('locationUpdated', handleLocationUpdate);
    
    return () => {
      window.removeEventListener('locationUpdated', handleLocationUpdate);
    };
  }, []);

  // CRITICAL FIX: Update local state when userLocation prop changes
  useEffect(() => {
    setLocalLocation(userLocation);
  }, [userLocation]);

  // Get location display info - use localLocation instead of userLocation
  const getLocationInfo = () => {
    if (!localLocation) {
      return {
        displayText: "No location set",
        accuracy: null,
        showUpdateButton: true
      };
    }

    // Determine accuracy level based on source
    let accuracy = 'approximate';
    let displayText = 'Unknown location';

    // ENHANCEMENT: Handle manual postcode entry
    if (localLocation.source === 'manual' && localLocation.postcode) {
      displayText = localLocation.postcode;
      accuracy = 'postcode';
    } else if (localLocation.city && localLocation.region) {
      displayText = `${localLocation.city}, ${localLocation.region}`;
    } else if (localLocation.city) {
      displayText = localLocation.city;
    } else if (localLocation.source === 'browser') {
      displayText = 'GPS location';
      accuracy = 'precise';
    }

    return {
      displayText,
      accuracy,
      showUpdateButton: true
    };
  };

  const locationInfo = getLocationInfo();

  const handleUpdateClick = () => {
    setShowUpdatePrompt(true);
    onUpdateLocation?.();
  };

  if (locationLoading) {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        <div className="animate-pulse flex items-center gap-1">
          <MapPin className="h-3 w-3 text-gray-400" />
          <span className="text-xs text-gray-500">Detecting...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <MapPin className={cn(
        "h-3 w-3 flex-shrink-0",
        localLocation ? "text-green-500" : "text-gray-400"
      )} />
      
      {/* Desktop version */}
      <div className="hidden sm:flex items-center gap-1">
        <span className={cn(
          "text-xs",
          theme === 'light' ? 'text-gray-600' : 'text-gray-400'
        )}>
          Your location: {locationInfo.displayText}
          {locationInfo.accuracy && (
            <span className="text-gray-500 ml-1">({locationInfo.accuracy})</span>
          )}
        </span>
        
        {locationInfo.showUpdateButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUpdateClick}
            className={cn(
              "h-5 px-1 text-xs underline hover:no-underline",
              theme === 'light' 
                ? 'text-purple-600 hover:text-purple-700 hover:bg-purple-50' 
                : 'text-purple-400 hover:text-purple-300 hover:bg-purple-900/20'
            )}
          >
            <Edit3 className="h-2 w-2 mr-1" />
            Update
          </Button>
        )}
      </div>

      {/* Mobile version */}
      <div className="sm:hidden flex items-center gap-1">
        <span className={cn(
          "text-xs",
          theme === 'light' ? 'text-gray-600' : 'text-gray-400'
        )}>
          {localLocation ? locationInfo.displayText.split(',')[0] : 'No location'}
        </span>
        
        {locationInfo.showUpdateButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUpdateClick}
            className={cn(
              "h-4 px-0.5 text-xs",
              theme === 'light' 
                ? 'text-purple-600 hover:text-purple-700' 
                : 'text-purple-400 hover:text-purple-300'
            )}
          >
            <Edit3 className="h-2 w-2" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default LocationDisplay;
